import { LibraryCache } from 'base/library_cache';
import { recordMap } from 'blocks/woot_woot/string_map';
import { computedFn } from 'mobx-utils';
import { anElementInstance, ElementInstance, Section } from '../model';
import {
  ReplaceComponentSuggestion,
  ReplaceElementSuggestion,
} from '../suggested_edits';
import {
  findElementReplacableTextContent,
  ReplaceableTextContent2,
} from './element_replacable_text_content';

/**
 * Maps text fields as appropriate.
 * Observable (based on loading library json)
 **/
export const makeReplaceComponentSuggestion = computedFn(
  (
    section: Section,
    newComponentSlug: string,
    library: LibraryCache,
  ): Pick<
    ReplaceComponentSuggestion,
    'type' | 'element' | 'needsToGenerateCopy'
  > => {
    const old = section.element.current;
    const oldFields = old && findElementReplacableTextContent(old, library);
    const unmappedNewElement = anElementInstance({
      source: { slug: newComponentSlug },
    });
    const newFields = findElementReplacableTextContent(
      unmappedNewElement,
      library,
    );
    const matchRes =
      oldFields && newFields && matchTextFields(oldFields, newFields);

    const newCommentThreads = new Map(unmappedNewElement.commentThreads);
    const newTextOverrides = new Map(unmappedNewElement.textOverrides);
    for (const [from, to] of matchRes?.mapping.entries() || []) {
      if (to.element !== unmappedNewElement) {
        // We only pass it 1 element - no sub elements (slotOverrides),
        // So this should never happen
        throw new Error('To refers to unknown element');
      }
      const oldText = from.element.textOverrides.get(from.nodeId);
      oldText && newTextOverrides.set(to.nodeId, oldText);

      for (const [
        threadId,
        anchoring,
      ] of from.element.commentThreads.entries()) {
        if (
          anchoring.nodeId === from.nodeId ||
          // If it was anchored to this element, it needs to move somewhere.
          // Best to move it to a text node in the same element.
          anchoring.nodeId == null
        ) {
          newCommentThreads.set(threadId, {
            ...anchoring.state,
            nodeId: to.nodeId,
          });
        }
      }
    }

    const mappedNewElement = {
      ...unmappedNewElement,
      commentThreads: recordMap(newCommentThreads),
      textOverrides: recordMap(newTextOverrides),
    };
    return {
      type: 'replace-component',
      element: mappedNewElement,
      needsToGenerateCopy:
        old != null &&
        old.textOverrides.size > 0 &&
        matchRes != null &&
        matchRes.needsToGenerateCopy,
    };
  },
);

export const makeReplaceElementSuggestion = computedFn(
  (
    old: ElementInstance,
    newElementSource: { slug: string; rootName?: string },
    library: LibraryCache,
  ): Pick<ReplaceElementSuggestion, 'type' | 'newElement'> => {
    const oldFields = findElementReplacableTextContent(old, library);
    const unmappedNewElement = anElementInstance({
      source: newElementSource,
    });
    const newFields = findElementReplacableTextContent(
      unmappedNewElement,
      library,
    );

    const newTextOverrides = new Map(unmappedNewElement.textOverrides);

    if (oldFields.length === 1 && newFields.length === 1) {
      const [from] = oldFields;
      const [to] = newFields;

      const oldText = from.element.textOverrides.get(from.nodeId);
      if (oldText) {
        newTextOverrides.set(to.nodeId, oldText);
      }
    }

    const mappedNewElement = {
      ...unmappedNewElement,
      textOverrides: recordMap(newTextOverrides),
      // Copy over comment threads and margins as-is
      commentThreads: recordMap(old.commentThreads.state),
      margin: old.margin,
    };

    return {
      type: 'replace-element',
      newElement: anElementInstance(mappedNewElement),
    };
  },
);

/**
 * Maps text fields between components where possible.  If the new component has unmatched
 * text fields then "needToGenerateCopy" will be true.
 **/
export function matchTextFields<
  A extends ReplaceableTextContent2<any>,
  B extends ReplaceableTextContent2<any>,
>(
  oldTextFields: readonly A[],
  newTextFields: readonly B[],
): { mapping: Map<A, B>; needsToGenerateCopy: boolean } {
  const newByText = new Map<string, B[]>();
  for (const a of newTextFields) {
    const aText = normalizeOriginalText(a.original);
    const list = newByText.get(aText) || [];
    list.push(a);
    newByText.set(aText, list);
  }

  const mapping = new Map<A, B>();
  for (const b of oldTextFields) {
    const bText = normalizeOriginalText(b.original);
    const list = newByText.get(bText);
    if (!list) {
      // If the new component has less text fields, that's ok
      continue;
    }
    mapping.set(b, list.shift()!);
    if (list.length === 0) newByText.delete(bText);
  }
  return { mapping, needsToGenerateCopy: newByText.size > 0 };
}

function normalizeOriginalText(text: string) {
  return text.replace(/\s+/g, ' ').toLowerCase().trim();
}
